import React, { useEffect, useRef } from "react";
import { formatStudioTime } from "../../utils/workforceTimeUtils";
import "./EndBreakClockOutSheet.css";

/**
 * EndBreakClockOutSheet — bottom-sheet confirmation for the combined
 * "End break and clock out" CTA.
 *
 * Authoritative spec:
 *   - docs/design/TIME-005-design.md §"Combined 'End break and clock out' CTA flow"
 *
 * The sheet does NOT fire the API directly — the parent (ClockInOutScreen)
 * handles the clock-out mutation on confirm. The server atomically ends the
 * open break as part of clock-out (TIME-004a server semantics; see
 * §"Cross-stack note for Marcus" in the design spec). That keeps the client
 * to a single mutation + spinner per the spec.
 *
 * Behaviour:
 *   - Open: focus moves to the Confirm button; Tab cycles to Cancel; Esc
 *     dismisses; backdrop click dismisses.
 *   - Reduced-motion: the slide-up animation is disabled by the CSS
 *     @media block; the sheet appears instantly.
 *   - role="dialog" with aria-labelledby referencing the heading.
 *
 * Today's totals (Worked / Break) are computed from the same data the
 * parent banner already has in scope:
 *   - Worked = (now - punchInTime) - totalBreakMinutesSoFar - currentBreak elapsed
 *   - Break  = totalBreakMinutesSoFar + currentBreak elapsed
 * The numbers freeze at sheet-open time (no live ticker inside the sheet
 * per spec — the sheet is a confirmation, not a status surface).
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string|Date} props.breakStartedAt
 * @param {string|Date} props.shiftStartedAt
 * @param {number} [props.totalBreakMinutesSoFar]  — sum of CLOSED breaks
 * @param {boolean} props.submitting
 * @param {() => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
const EndBreakClockOutSheet = ({
  open,
  breakStartedAt,
  shiftStartedAt,
  totalBreakMinutesSoFar = 0,
  submitting = false,
  onConfirm,
  onCancel,
}) => {
  const confirmRef = useRef(null);
  const headingId = "end-break-clock-out-heading";

  useEffect(() => {
    if (!open) return undefined;
    // Focus the Confirm button on open per spec accessibility section.
    if (confirmRef.current) {
      confirmRef.current.focus();
    }
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) {
        e.preventDefault();
        onCancel?.();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, submitting, onCancel]);

  if (!open) return null;

  // Frozen-at-open-time totals. We re-compute on each render but the inputs
  // (breakStartedAt, shiftStartedAt, totalBreakMinutesSoFar) only change
  // when the parent re-mounts the sheet, so values are effectively pinned.
  const nowDate = new Date();
  const breakStartFormatted = formatStudioTime(breakStartedAt);
  const nowFormatted = formatStudioTime(nowDate);

  // Total minutes since shift start.
  const shiftStartMs = shiftStartedAt
    ? new Date(shiftStartedAt).getTime()
    : NaN;
  const totalSinceClockInMin = Number.isFinite(shiftStartMs)
    ? Math.max(0, Math.floor((nowDate.getTime() - shiftStartMs) / 60000))
    : 0;

  // Current open-break minutes.
  const breakStartMs = breakStartedAt
    ? new Date(breakStartedAt).getTime()
    : NaN;
  const currentBreakMin = Number.isFinite(breakStartMs)
    ? Math.max(0, Math.floor((nowDate.getTime() - breakStartMs) / 60000))
    : 0;

  const totalBreakMin = (totalBreakMinutesSoFar || 0) + currentBreakMin;
  const workedMin = Math.max(0, totalSinceClockInMin - totalBreakMin);

  const fmtMins = (m) => {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${String(h).padStart(2, "0")}h ${String(mm).padStart(2, "0")}m`;
  };

  const handleBackdropClick = () => {
    if (!submitting) onCancel?.();
  };

  // Stop propagation so clicks inside the sheet don't dismiss via the
  // backdrop handler.
  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="ebco-sheet__backdrop"
      role="presentation"
      onClick={handleBackdropClick}
      data-testid="end-break-clock-out-backdrop"
    >
      <div
        className="ebco-sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onClick={stop}
        data-testid="end-break-clock-out-sheet"
      >
        <h2 id={headingId} className="ebco-sheet__heading">
          End break and clock out?
        </h2>

        <p className="ebco-sheet__body">
          You'll end your break (started at{" "}
          <span className="ebco-sheet__inline-time">
            {breakStartFormatted}
          </span>
          ) and clock out for the day at{" "}
          <span className="ebco-sheet__inline-time">{nowFormatted}</span>.
        </p>

        <div className="ebco-sheet__totals">
          <p className="ebco-sheet__totals-title">Today's totals:</p>
          <p className="ebco-sheet__totals-row">
            <span className="ebco-sheet__totals-label">Worked:</span>
            <span
              className="ebco-sheet__totals-value"
              data-testid="ebco-sheet-worked"
            >
              {fmtMins(workedMin)}
            </span>
          </p>
          <p className="ebco-sheet__totals-row">
            <span className="ebco-sheet__totals-label">Break: </span>
            <span
              className="ebco-sheet__totals-value"
              data-testid="ebco-sheet-break"
            >
              {fmtMins(totalBreakMin)}
            </span>
          </p>
        </div>

        <button
          ref={confirmRef}
          type="button"
          className="ebco-sheet__confirm"
          onClick={onConfirm}
          disabled={submitting}
          data-testid="end-break-clock-out-confirm"
        >
          {submitting ? "Clocking out..." : "End break and clock out"}
        </button>

        <button
          type="button"
          className="ebco-sheet__cancel"
          onClick={onCancel}
          disabled={submitting}
          data-testid="end-break-clock-out-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default EndBreakClockOutSheet;
